/**
 * Clipboard Utilities - Helper functions for clipboard operations
 * 
 * Provides modern clipboard functionality with proper error handling
 * and browser compatibility checks.
 * 
 * @author CTS - Ford PM UI Team
 */

/**
 * Copy text to clipboard using modern Clipboard API
 * 
 * @param {string} text - Text to copy to clipboard
 * @param {Event} event - Click event (optional, for stopping propagation)
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const copyToClipboard = async (text, event = null) => {
  // Stop event propagation if event is provided
  if (event) {
    event.stopPropagation();
  }
  
  // Check if Clipboard API is available
  if (!navigator.clipboard) {
    console.warn('Clipboard API not available in this browser');
    return false;
  }
  
  try {
    await navigator.clipboard.writeText(text);
    console.log('Text copied to clipboard:', text);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

/**
 * Check if clipboard functionality is available
 * 
 * @returns {boolean} - True if clipboard API is available
 */
export const isClipboardAvailable = () => {
  return !!(navigator.clipboard?.writeText);
};

/**
 * Copy text with user feedback (can be extended for toast notifications)
 * 
 * @param {string} text - Text to copy
 * @param {Event} event - Click event (optional)
 * @param {Function} onSuccess - Success callback (optional)
 * @param {Function} onError - Error callback (optional)
 * @returns {Promise<boolean>} - Returns true if successful
 */
export const copyWithFeedback = async (text, event = null, onSuccess = null, onError = null) => {
  const success = await copyToClipboard(text, event);
  
  if (success && onSuccess) {
    onSuccess(text);
  } else if (!success && onError) {
    onError(text);
  }
  
  return success;
};

/**
 * Default export for convenience
 */
const clipboardUtils = {
  copyToClipboard,
  isClipboardAvailable,
  copyWithFeedback
};

export default clipboardUtils;
